/**
 * FailedConnectionPrompt - displays options for handling a failed connection
 *
 * When a connection fails during session validation, this component presents
 * the user with options to either keep the connection (view queries offline)
 * or remove it from the session.
 */

import { AlertTriangle, EyeOff, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';

// ============================================================================
// Types
// ============================================================================

/**
 * User decision for handling a failed connection
 */
export type ConnectionDecision = 'keep' | 'remove' | 'pending';

interface FailedConnectionPromptProps {
  /** Connection name to display */
  connectionName: string;
  /** Server/host info */
  serverInfo?: string;
  /** Error message from failed connection */
  error: string;
  /** Current decision state */
  decision: ConnectionDecision;
  /** Whether a retry is in progress */
  isRetrying?: boolean;
  /** Called when user chooses to retry connection */
  onRetry: () => void;
  /** Called when user chooses to edit the connection */
  onEdit: () => void;
  /** Called when user chooses to keep connection (view offline) */
  onKeep: () => void;
  /** Called when user chooses to remove connection */
  onRemove: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function FailedConnectionPrompt({
  connectionName,
  serverInfo,
  error,
  decision,
  isRetrying = false,
  onRetry,
  onEdit,
  onKeep,
  onRemove,
}: FailedConnectionPromptProps) {
  const isDecided = decision !== 'pending';

  return (
    <div
      className={cn(
        'p-3 rounded-md transition-colors',
        isDecided ? 'bg-muted/30' : 'bg-destructive/5 border border-destructive/20'
      )}
      data-testid="failed-connection-prompt"
      data-decision={decision}
    >
      {/* Connection Info */}
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="space-y-0.5">
            <div className="font-medium text-sm leading-snug break-words whitespace-normal">
              {connectionName}
            </div>
            {serverInfo && (
              <div className="text-xs text-muted-foreground break-all whitespace-normal">
                {serverInfo}
              </div>
            )}
          </div>
          <p className="text-xs text-destructive mt-1 break-words whitespace-normal" title={error}>
            {error}
          </p>
        </div>
      </div>

      {/* Decision Buttons or Status */}
      {isRetrying ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          <span>Retrying connection...</span>
        </div>
      ) : decision === 'pending' ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground mb-1">
            What would you like to do with this connection?
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="flex-1"
              data-testid="retry-connection-button"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Try again
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="flex-1"
              data-testid="edit-connection-button"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onKeep}
              className="flex-1"
              data-testid="keep-connection-button"
            >
              <EyeOff className="h-3.5 w-3.5 mr-1.5" />
              Keep offline
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onRemove}
              className="flex-1 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
              data-testid="remove-connection-button"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {decision === 'keep' && (
            <>
              <EyeOff className="h-3.5 w-3.5" />
              <span>Will be kept offline - queries will be viewable but not executable</span>
            </>
          )}
          {decision === 'remove' && (
            <>
              <Trash2 className="h-3.5 w-3.5" />
              <span>Will be removed - tabs for this connection will not be loaded</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
